import {
  Button,
  Modal,
  Flex,
  Label,
  LabeledInput,
  LabeledSelect,
  ProgressRadial,
  Text,
} from "@itwin/itwinui-react";
import { useCallback, useEffect, useState } from "react";
import { usePatchSubscription } from "../../hooks/usePatchSubscription/usePatchSubscription";
import { ISubscriptionCosmosEntity } from "../../interfaces/IPagedAdminConsumers";
import { IProducer } from "../../interfaces/IProducer";
import { ISubscriptionCreateRequest } from "../../interfaces/ISubscriptionCreateRequest";
import { EventSelector } from "../eventSelector/eventSelector";
import { DataCenterSelect } from "../dataCenterSelect/dataCenterSelect";
import { useGetDataCenters } from "../../hooks/useGetDataCenters/useGetDataCenters";

interface UpdatedSubscriptionState extends Omit<ISubscriptionCreateRequest, "producerId"> {
  useAllEvents: boolean;
}

export const SubscriptionEditModal = (props: {
  subscription: ISubscriptionCosmosEntity;
  producer: IProducer;
  modalIsOpen: boolean;
  isAdmin?: boolean;
  setModalIsOpen: (open: boolean) => void;
  refreshSubscriptions: () => void;
}) => {
  const {
    subscription,
    producer,
    modalIsOpen,
    isAdmin,
    setModalIsOpen,
    refreshSubscriptions,
  } = props;

  const [data, loading, error, patchConsumer] = usePatchSubscription();

  const [dataCentersResponse, dataCentersLoading, dataCentersError, getDataCenters] = useGetDataCenters();

  // Fetch data centers on mount
  useEffect(() => {
    getDataCenters();
  }, [getDataCenters]);

  // Determine if the subscription was originally using "all events" (subscribesToFutureEvents from API, or events: [] from admin)
  const originalUseAllEvents = subscription.subscribesToFutureEvents || subscription.events.length === 0;

  const [updatedSubscription, setUpdatedSubscription] = useState<UpdatedSubscriptionState>({
    status: subscription.status,
    accountIdFilter: subscription.accountIdFilter,
    iTwinIdFilter: subscription.itwinIdFilter,
    resourceDataCenterIdFilter: subscription.resourceDataCenterIdFilter,
    iTwinDataCenterIdFilter: subscription.iTwinDataCenterIdFilter,
    // If original events was [], treat as "all events" mode
    events: originalUseAllEvents
      ? producer.eventDefinitions.map((x) => x.eventType)
      : subscription.events,
    useAllEvents: originalUseAllEvents,
  });

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Disabled", label: "Disabled" },
  ];

  const handleEventsChange = useCallback((events: string[], useAllEvents: boolean) => {
    setUpdatedSubscription((prev) => ({
      ...prev,
      events,
      useAllEvents,
    }));
  }, []);

  const updateSub = async () => {
    // When saving, if useAllEvents is true, send empty array to API
    const requestBody: Omit<ISubscriptionCreateRequest, "producerId"> = {
      status: updatedSubscription.status,
      accountIdFilter: updatedSubscription.accountIdFilter,
      iTwinIdFilter: updatedSubscription.iTwinIdFilter,
      resourceDataCenterIdFilter: updatedSubscription.resourceDataCenterIdFilter,
      iTwinDataCenterIdFilter: updatedSubscription.iTwinDataCenterIdFilter,
      events: updatedSubscription.useAllEvents ? [] : updatedSubscription.events,
      // Explicitly indicate future events preference
      subscribesToFutureEvents: updatedSubscription.useAllEvents,
    };

    let result = await patchConsumer(
      subscription.consumerId,
      subscription.id,
      requestBody,
      isAdmin ?? false
    );
    if (result?.error === undefined) {
      setModalIsOpen(false);
      refreshSubscriptions();
    }
  };

  // Validation: either useAllEvents is true, or there are selected events
  const canSave = updatedSubscription.useAllEvents || updatedSubscription.events.length > 0;

  return (
    <Modal
      isOpen={modalIsOpen}
      onClose={() => setModalIsOpen(false)}
      closeOnEsc
      isDismissible
      title="Edit Subscription"
    >
      <Label>Events</Label>
      <EventSelector
        eventDefinitions={producer?.eventDefinitions ?? []}
        selectedEvents={updatedSubscription.events}
        useAllEvents={updatedSubscription.useAllEvents}
        onEventsChange={handleEventsChange}
      />

      <div style={{ marginTop: "16px" }}>
        <LabeledSelect
          label="Status"
          options={statusOptions}
          value={updatedSubscription.status}
          onChange={(value) =>
            setUpdatedSubscription({
              ...updatedSubscription,
              status: value,
            })
          }
          style={{ marginBottom: "12px", width: "50%" }}
        />
      </div>
      <Flex flexDirection="row" style={{ width: "100%" }} flexWrap="wrap">
        <Flex.Item flex="1 1 33%">
          <LabeledInput
            label="Account Id Filter"
            value={updatedSubscription.accountIdFilter}
            onChange={(event) =>
              setUpdatedSubscription({
                ...updatedSubscription,
                accountIdFilter: event.target.value,
              })
            }
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <LabeledInput
            label="iTwin Id Filter"
            value={updatedSubscription.iTwinIdFilter}
            onChange={(event) =>
              setUpdatedSubscription({
                ...updatedSubscription,
                iTwinIdFilter: event.target.value,
              })
            }
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <DataCenterSelect
            label="Resource Data Center Id"
            value={updatedSubscription.resourceDataCenterIdFilter}
            onChange={(value) =>
              setUpdatedSubscription({
                ...updatedSubscription,
                resourceDataCenterIdFilter: value,
              })
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <DataCenterSelect
            label="iTwin Data Center Id"
            value={updatedSubscription.iTwinDataCenterIdFilter}
            onChange={(value) =>
              setUpdatedSubscription({
                ...updatedSubscription,
                iTwinDataCenterIdFilter: value,
              })
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
      </Flex>

      <Flex flexDirection="row">
        <Button
          onClick={() => updateSub()}
          disabled={!canSave}
        >
          Save
        </Button>

        {!canSave && (
          <Text style={{ color: "red", marginLeft: "12px" }}>
            Subscriptions must have events. To stop receiving events please
            disable the subscription, or remove it from the consumer.
          </Text>
        )}
      </Flex>

      {error !== undefined || data?.error?.message !== undefined ? (
        <Text style={{ color: "red" }}>
          {data?.error?.message ?? error?.message ?? "Unknown error"}
        </Text>
      ) : (
        ""
      )}
      {loading ? (
        <ProgressRadial indeterminate style={{ marginLeft: "20px" }} />
      ) : (
        ""
      )}
    </Modal>
  );
};
